<template>
  <div :class="`wizard ${isOpen ? 'wizard--open' : ''}`">
    <transition name="opacity">
      <div class="wizard__overlay" v-if="isOpen" />
    </transition>

    <transition name="translate">
      <div :class="`wizard__dialog ${fullscreenOnMobile ? 'wizard__dialog--fullscreen' : ''}`" v-if="isOpen">
        <div class="wizard__dialog__image" v-if="image">
          <transition name="fade">
            <picture :key="currentStep" alt>
              <source :key="source.media" v-for="source in image.sources" :media="source.media" :srcset="source.srcset">
              <img :src="image.default" alt>
            </picture>
          </transition>
        </div>

        <div :class="`wizard__dialog__content ${image ? 'wizard__dialog__content--image' : ''}`">
          <h1 v-html="title" />
          <p v-html="text" />
        </div>

        <btn :flat="steps.length <= 1" :outlined="steps.length > 1" @click="nextStep">
          {{ steps.length > 1 ? (currentStep === steps.length - 1 ? 'Entendi!' : 'Próximo') : 'Continuar' }}
        </btn>

        <div class="wizard__dialog__stepper" v-if="steps.length > 1">
          <div :key="i" v-for="(_, i) in steps" :class="i === currentStep ? 'active' : ''" />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'wizard',
  props: {
    steps: Array,
    fullscreenOnMobile: Boolean
  },
  data: () => ({
    currentStep: 0,
    isOpen: false
  }),
  watch: {
    isOpen(val) {
      if (!val) {
        document.body.style.overflow = 'auto'
      } else {
        document.body.style.overflow = 'hidden'
      }
    }
  },
  computed: {
    step() {
      return this.steps[this.currentStep]
    },
    image() {
      return this.step.image
    },
    title() {
      return this.step.title
    },
    text() {
      return this.step.text
    }
  },
  methods: {
    nextStep() {
      if (this.currentStep + 1 >= this.steps.length) {
        this.isOpen = false
        this.$emit('close')
        return
      }

      this.currentStep++
    }
  },
  mounted() {
    this.isOpen = true
  }
}
</script>

<style lang="scss">
.wizard {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 1000;
  pointer-events: none;

  &__overlay {
    position: absolute;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.65);
    transition: opacity 0.2s;

    &.opacity-enter-active, &.opacity-leave-active {
      opacity: 1
    }

    &.opacity-enter, &.opacity-leave-to {
      opacity: 0;
    }
  }

  &__dialog {
    position: relative;
    overflow: hidden;
    border-top-left-radius: 24px;
    border-top-right-radius: 24px;
    background-color: white;
    display: flex;
    flex-direction: column;
    max-height: calc(100% - 32px);
    min-height: 75%;
    width: 100%;
    transition: transform .2s;

    &--fullscreen {
      border-radius: 0;
      min-height: 100%;
    }

    &.translate-enter-active, &.translate-leave {
      transform: none;
    }

    &.translate-enter, &.translate-leave-active {
      transform: translateY(100vh);
    }

    &__image {
      position: relative;
      height: 60vw;
      max-height: 300px;

      & > picture {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        transition: opacity .2s;

        &.fade-enter-active, &.fade-leave {
          opacity: 1;
        }

        &.fade-enter, &.fade-leave-active {
          opacity: 0;
        }

        & > img {
          height: 100%;
          width: 100%;
          object-fit: cover;
          object-position: center;
        }
      }
    }

    &__content {
      text-align: center;
      padding: 48px 24px 0 24px;
      flex-grow: 1;

      &--image {
        padding: 32px 24px 0 24px;
      }

      & > h1 {
        font-size: 22px;
        margin-bottom: 24px;
        margin-top: 0;
        line-height: 1.3;
      }

      & > p {
        font-weight: normal;
        font-size: 16px;
        margin-top: 0;
        margin-bottom: 0;
        line-height: 1.4;

        & > b {
          color: #A217AB;
        }
      }
    }

    & > .btn {
      margin: 24px;
      margin-bottom: 32px;
      width: calc(100% - 40px);
      height: auto;

      &:last-child {
        margin-bottom: 48px
      }
    }

    &__stepper {
      display: flex;
      margin: 48px auto;
      margin-top: 0;

      & > div {
        width: 10px;
        height: 10px;
        border-radius: 100%;
        margin: 0 8px;
        background: rgba(69, 61, 77, 0.7);
        transform: scale(0.5);
        transition: transform 0.2s;

        &.active {
          transform: none;
          background: linear-gradient(180deg, #EB9CFF, #A217AB);
        }
      }
    }
  }

  &--open {
    pointer-events: all;
  }

  @media screen and (min-width: 768px) {
    align-items: center;

    &__dialog {
      width: auto;
      max-width: 650px;
      border-radius: 32px;
      min-height: auto;

      &__image {
        height: 260px;
      }

      &__content {
        padding: 60px 100px 0 100px;

        &--image {
          padding: 30px 60px 0 60px;
        }

        & > h1 {
          font-size: 30px;
          margin-bottom: 16px;
        }

        & > p {
          font-size: 18px;
        }
      }

      & > .btn {
        width: calc(100% - 240px);
        margin: 24px auto;

        &:last-child {
          margin-bottom: 60px;
        }
      }

      &__stepper {
        margin-bottom: 32px;
      }
    }
  }
}
</style>
